import React from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import PhoneInput, { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import '../../../styles/PhoneNumberInputGlobal.css';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldMultiSelect,
  FieldLabelTooltip,
} from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import css from './ShippingDetailsForm.module.css';
import getCountryCodes from '../../../translations/countryCodes';
import { WeightUnitEnum } from '../../../config/configExtras';
import DeliveryOptions from './DeliveryOptions';
import { PLATFORM } from '../../../util/constants';

const ShippingDetailsForm = props => (
  <FinalForm
    {...props}
    keepDirtyOnReinitialize
    render={({
      className,
      rootClassName,
      disabled,
      handleSubmit,
      formId,
      invalid,
      ready,
      values,
      form,
      inProgress,
    }) => {
      const config = useConfiguration();
      const intl = useIntl();
      const { invalidPhoneValue, phone } = values || {};
      const classes = classNames(rootClassName || css.root, className);
      const submitDisabled = invalid || disabled || inProgress;
      const countryCodes = getCountryCodes(config.localization.locale);

      const fields = [
        { name: 'length', type: 'number', autoComplete: 'given-name', messageId: 'length' },
        { name: 'width', type: 'number', autoComplete: 'given-name', messageId: 'width' },
        { name: 'height', type: 'number', autoComplete: 'given-name', messageId: 'height' },
        { name: 'weight', type: 'number', autoComplete: 'given-name', messageId: 'weight' },
        {
          name: 'street1',
          type: 'text',
          autoComplete: 'street-address',
          messageId: 'streetAddress',
        },
        {
          name: 'street2',
          type: 'text',
          autoComplete: 'address-line1',
          messageId: 'buildingNumber',
        },
        { name: 'city', type: 'text', autoComplete: 'address-level2', messageId: 'city' },
        { name: 'state', type: 'text', autoComplete: 'address-level1', messageId: 'state' },
        { name: 'zip', type: 'text', autoComplete: 'postal-code', messageId: 'postalCode' },
      ];

      const renderField = ({ name, type, autoComplete, messageId }) => {
        const isRequired = name !== 'street2';

        return (
          <div className={css.formFld} key={name}>
            <FieldLabelTooltip
              tooltipId={`${messageId}Tooltip`}
              fieldId={messageId}
              caption={intl.formatMessage({ id: `ShippingDetailsForm.${messageId}` })}
              text={intl.formatMessage({
                id: `ShippingDetailsForm.${messageId}Tooltip`,
              })}
            />

            <FieldTextInput
              type={type}
              id={formId ? `${formId}.${name}` : name}
              name={name}
              autoComplete={autoComplete}
              placeholder={intl.formatMessage({
                id: `ShippingDetailsForm.${messageId}Placeholder`,
              })}
              validate={
                isRequired
                  ? validators.required(
                      intl.formatMessage({ id: `ShippingDetailsForm.${messageId}Required` })
                    )
                  : undefined
              }
            />
          </div>
        );
      };

      const renderDefaultPackageSize = (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            {intl.formatMessage({
              id: 'ShippingDetailsForm.defaultPackageSize',
            })}
          </h3>
          {[['length', 'width'], ['height', 'weight']].map((pair, index) => (
            <div className={css.formRow} key={index}>
              {pair.map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                return field ? renderField(field) : null;
              })}
            </div>
          ))}
          <div className={css.formRow}>
            <div className={css.formFld}>
              <FieldLabelTooltip
                tooltipId="unitTooltip"
                fieldId="unit"
                caption={intl.formatMessage({ id: 'ShippingDetailsForm.weightUnitLabel' })}
                text={intl.formatMessage({
                  id: 'ShippingDetailsForm.weightUnitTooltip',
                })}
              />

              <Field
                name="unit"
                component={FieldMultiSelect}
                options={WeightUnitEnum}
                isMulti={false}
                validate={validators.required(
                  intl.formatMessage({ id: 'ShippingDetailsForm.weightUnitRequired' })
                )}
              />
            </div>
          </div>
        </div>
      );

      const renderSenderAddress = (
        <div className={css.section}>
          <h3 className={css.sectionTitle}>
            {intl.formatMessage({
              id: 'ShippingDetailsForm.senderAddress',
            })}
          </h3>
          {[['street1', 'street2'], ['city', 'state'], ['zip']].map((pair, index) => (
            <div className={css.formRow} key={index}>
              {pair.map(fieldName => {
                const field = fields.find(f => f.name === fieldName);
                return field ? renderField(field) : null;
              })}
            </div>
          ))}
          <div className={css.formRow}>
            <div className={css.formFld}>
              <div className={css.customSelect}>
                <FieldLabelTooltip
                  tooltipId="countryTooltip"
                  fieldId="country"
                  caption={intl.formatMessage({ id: 'ShippingDetailsForm.country' })}
                  text={intl.formatMessage({
                    id: 'ShippingDetailsForm.countryTooltip',
                  })}
                />

                <Field
                  name="country"
                  component={FieldMultiSelect}
                  options={countryCodes}
                  isMulti={false}
                  validate={validators.required(
                    intl.formatMessage({ id: 'ShippingDetailsForm.countryRequired' })
                  )}
                />
              </div>
            </div>
            <div className={css.formFld}>
              <FieldLabelTooltip
                tooltipId="phoneTooltip"
                fieldId="phone"
                caption={intl.formatMessage({ id: 'ShippingDetailsForm.phone'})}
                text={intl.formatMessage({
                  id: 'ShippingDetailsForm.phoneTooltip',
                })}
              />

              <Field name="phone">
                {({ input }) => (
                  <div>
                    <PhoneInput
                      {...input}
                      format={formatPhoneNumberIntl}
                      placeholder={intl.formatMessage({
                        id: 'ShippingDetailsForm.phonePlaceholder',
                      })}
                      defaultCountry="US"
                      onChange={value => {
                        input.onChange(value);
                        const formattedNumber = formatPhoneNumberIntl(value);
                        if (isValidPhoneNumber(formattedNumber)) {
                          form.change('invalidPhoneValue', false);
                        } else {
                          form.change('invalidPhoneValue', true);
                        }
                      }}
                    />
                  </div>
                )}
              </Field>
              {invalidPhoneValue && phone && (
                <span className={css.invalidPhoneValue}>
                  {intl.formatMessage({ id: 'ShippingDetailsForm.inivalidPhoneNumber' })}
                </span>
              )}
            </div>
          </div>
        </div>
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <p className={css.shippingPreference}>
            {intl.formatMessage({ id: 'ShippingDetailsForm.shippingPreference' })}
            <FieldLabelTooltip
              tooltipId="shippingPreferenceTooltip"
              fieldId="gender"
              className={css.shippingPreferenceIcon}
              text={intl.formatMessage({
                id: 'ShippingDetailsForm.shippingPreferenceTooltip',
              })}
            />
          </p>
          <DeliveryOptions form={form} intl={intl} onVerifyShippingPreferences={() => {}} />
          {values?.shippingPreference === PLATFORM ? (
            <>
              {renderDefaultPackageSize}
              {renderSenderAddress}
            </>
          ) : null}
          <PrimaryButton
            className={css.submitButton}
            type="submit"
            inProgress={inProgress}
            disabled={submitDisabled}
            ready={ready}
          >
            {intl.formatMessage({ id: 'ShippingDetailsForm.saveDetails' })}
          </PrimaryButton>
        </Form>
      );
    }}
  />
);

export default ShippingDetailsForm;
